import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { openGuestCheckout } from "@/hooks/useGuestCheckout";
import { getLoginUrlForApiPlan } from "@/const";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useLocale } from "@/hooks/useLocale";
import { trackCheckoutStart } from "@/lib/analytics";

type ToolPlan = "day_pass" | "monthly";
type ApiPlan = "premium" | "ultra" | "mega";

/**
 * Homepage-level pricing summary, placed above FAQ/Blog: real prices
 * for both the tool side (Day Pass / Monthly, same billing.createCheckoutSession
 * flow as UsageAndUpgradeStrip) and the API/MCP side (Premium/Ultra/Mega,
 * apiKeys.createCheckoutSession). Free tiers are listed for context but
 * get no button -- there's nothing to buy.
 *
 * The two checkout flows genuinely differ in one important way: an API
 * plan upgrade requires an existing API key first (the server's own
 * apiKeys.createCheckoutSession enforces this -- see server/routers.ts),
 * while a tool plan doesn't. Rather than always bouncing an API "Quick
 * Buy" to /developers (annoying for someone who already has a key), it
 * tries the real checkout first and only redirects if the server
 * confirms there's no key yet.
 */
export function TransparentPricingBanner() {
  const { isAuthenticated } = useAuth();
  const { strings } = useLocale();
  const p = strings.pricingBanner;
  const [loadingPlan, setLoadingPlan] = useState<ToolPlan | ApiPlan | null>(null);

  const toolCheckout = trpc.billing.createCheckoutSession.useMutation();
  const apiCheckout = trpc.apiKeys.createCheckoutSession.useMutation();

  const handleToolUpgrade = async (plan: ToolPlan) => {
    if (!isAuthenticated) {
      openGuestCheckout(plan);
      return;
    }
    setLoadingPlan(plan);
    try {
      const result = await toolCheckout.mutateAsync({ plan });
      trackCheckoutStart(plan, "professional");
      window.location.href = result.url;
    } catch (error) {
      console.error("Failed to start checkout:", error);
      toast.error("Couldn't start checkout. Please try again in a moment.");
      setLoadingPlan(null);
    }
  };

  const handleApiUpgrade = async (plan: ApiPlan) => {
    if (!isAuthenticated) {
      // Carry the chosen plan through Google sign-in; the page opens Stripe Checkout for it on return (useAutoResumeCheckout).
      window.location.href = getLoginUrlForApiPlan(plan);
      return;
    }
    setLoadingPlan(plan);
    try {
      const result = await apiCheckout.mutateAsync({ plan });
      trackCheckoutStart("api", "api");
      window.location.href = result.url;
    } catch (error) {
      // Checkout no longer needs a key first (a first-time buyer gets theirs on return); this is a genuine failure,
      // so send them to /developers rather than show a dead-end error.
      console.error("Failed to start API checkout:", error);
      window.location.href = "/developers";
    }
  };

  const toolPlans: { key: ToolPlan; detail: string }[] = [
    { key: "day_pass", detail: p.dayPassDetail },
    { key: "monthly", detail: p.monthlyDetail },
  ];

  const apiPlans: { key: ApiPlan; detail: string }[] = [
    { key: "premium", detail: p.premiumDetail },
    { key: "ultra", detail: p.ultraDetail },
    { key: "mega", detail: p.megaDetail },
  ];

  return (
    <section className="container py-10">
      <div
        className="max-w-4xl mx-auto rounded-2xl border-2 p-6 sm:p-8"
        style={{ borderColor: "color-mix(in srgb, var(--signal) 30%, transparent)", backgroundColor: "color-mix(in srgb, var(--signal) 6%, transparent)" }}
      >
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: "var(--signal)" }}>
            {p.title}
          </h2>
          <p className="text-muted-foreground">{p.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Tools */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: "var(--signal)" }}>
              {p.toolPricingTitle}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm">
                  <span className="font-semibold">{p.freeLabel}</span> -- {p.freeToolDetail}
                </span>
              </div>
              {toolPlans.map(({ key, detail }) => (
                <div key={key}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold" style={{ color: "var(--signal)" }}>
                      {detail}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-3 text-xs shrink-0"
                      style={{ borderColor: "var(--signal)", color: "var(--signal)" }}
                      disabled={loadingPlan !== null}
                      onClick={() => handleToolUpgrade(key)}
                    >
                      {loadingPlan === key && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                      {strings.usageStrip.quickBuy}
                    </Button>
                  </div>
                  {key === "monthly" && (
                    <p className="text-xs text-muted-foreground mt-1">{p.monthlySavingsNote}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* API & MCP */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: "var(--signal)" }}>
              {p.apiPricingTitle}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm">
                  <span className="font-semibold">{p.freeLabel}</span> -- {p.freeApiDetail}
                </span>
              </div>
              {apiPlans.map(({ key, detail }) => (
                <div key={key} className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold" style={{ color: "var(--signal)" }}>
                    {detail}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-3 text-xs shrink-0"
                    style={{ borderColor: "var(--signal)", color: "var(--signal)" }}
                    disabled={loadingPlan !== null}
                    onClick={() => handleApiUpgrade(key)}
                  >
                    {loadingPlan === key && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                    {strings.usageStrip.quickBuy}
                  </Button>
                </div>
              ))}
              {!isAuthenticated && <p className="text-xs text-muted-foreground" data-testid="api-signin-note">Sign in with Google to create your API key.</p>}
            </div>
          </div>
        </div>
        <p className="mt-6 pt-5 border-t border-border/50 text-center text-sm text-muted-foreground">
          <a href="/premium-apps" className="underline">Premium Applications</a>: $3 day pass or $9.99/month.{" "}
          <a href="/workflow-products" className="underline">Workflow Products</a>: $29.99/month, 7-day trial.
        </p>
      </div>
    </section>
  );
}
